/**
 * What the workflow is doing, while it does it.
 *
 * ## ⚠️ This is not decoration
 *
 * A twenty-page contract is a vision call per page and takes a minute or more. A spinner over that
 * is dead air in front of a customer — and worse, it is dead air that looks identical to a hang.
 * Showing the scatter → map → reduce shape makes the wait the interesting part: *this* is what an
 * agentic pipeline actually does, and the per-page counter is the proof it is doing it in parallel.
 *
 * ## Reading the counter honestly
 *
 * `iteration_next` fires BEFORE each pass and never after the last, so its `index` is "passes
 * finished", not "pass number" — and it never reaches the total on its own. `run-state.ts` closes
 * the gap on `iteration_completed`. The total itself is the DECLARED pass count; on a failure the
 * engine reports what it intended rather than what it managed, so it is a progress hint and never a
 * result.
 */

import type { RunState, Step } from '../gateway/run-state.js'

/**
 * The stages, in the order the graph runs them.
 *
 * ⚠️ Matched on the node's TITLE, which is authored in the workflow and can be renamed. A stage
 * that fails to match still renders — it just arrives under its own title in graph order rather
 * than with a friendly caption. That degradation is deliberate: a demo whose progress view empties
 * itself because somebody renamed a node is worse than one showing raw titles.
 */
const CAPTIONS: { match: RegExp; caption: string }[] = [
  { match: /split/i, caption: 'Rendering each page' },
  { match: /prepare/i, caption: 'Reading the parties and the rule' },
  { match: /each page|iterate/i, caption: 'Checking every page' },
  { match: /fetch/i, caption: 'Fetching the page image' },
  { match: /check/i, caption: 'Looking for signatures' },
  { match: /aggregate|report/i, caption: 'Building the report' },
]

const captionFor = (step: Step): string =>
  CAPTIONS.find((c) => c.match.test(step.title))?.caption ?? step.title

export function Pipeline({ state }: { state: RunState }) {
  if (state.phase === 'idle') return null

  return (
    <div className="pipeline" data-phase={state.phase}>
      <ol className="pipeline__steps">
        {state.steps.map((step) => (
          <li key={step.nodeId} className={`pipeline__step is-${step.status}`} data-step={step.nodeId}>
            <span className="pipeline__dot" aria-hidden="true" />
            <span className="pipeline__caption">{captionFor(step)}</span>

            {step.progress !== null && step.progress.total > 0 && (
              <span className="pipeline__progress">
                <span
                  className="pipeline__bar"
                  style={{ width: `${Math.min(100, (step.progress.done / step.progress.total) * 100)}%` }}
                />
                <span className="pipeline__count">
                  {step.progress.done} of {step.progress.total} pages
                </span>
              </span>
            )}

            {step.elapsed !== null && <span className="pipeline__time">{step.elapsed.toFixed(1)}s</span>}
            {step.error !== null && <span className="pipeline__error">{summarise(step.error)}</span>}
          </li>
        ))}
      </ol>

      {state.phase === 'running' && state.steps.length === 0 && (
        <p className="pipeline__waiting">Starting…</p>
      )}

      {/* Surfaced rather than swallowed: a stream saying something new is worth knowing about. */}
      {state.unknownFrames > 0 && (
        <p className="pipeline__note">
          {state.unknownFrames === 1
            ? '1 event in this run was of a kind this demo does not recognise.'
            : `${state.unknownFrames} events in this run were of a kind this demo does not recognise.`}{' '}
          The result is unaffected.
        </p>
      )}
    </div>
  )
}

/**
 * The first meaningful line of an engine error.
 *
 * A failed code node's error is a multi-line Python traceback; the last line is the exception and
 * the rest is the sandbox's file paths. Showing all of it in a progress row buries everything else.
 */
function summarise(error: string): string {
  const lines = error.trim().split('\n').filter(Boolean)
  const last = lines[lines.length - 1] ?? error
  return last.length > 160 ? `${last.slice(0, 157)}…` : last
}
